import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api } from '../lib/apiClient';

interface UseWhatsAppMessagesOptions {
  sinceHours?: number;
  contactName?: string;
}

interface SendMessageInput {
  text: string;
}

export function useWhatsAppMessages(
  jid: string | undefined,
  options: UseWhatsAppMessagesOptions = {}
) {
  const queryClient = useQueryClient();
  const { contactName } = options;
  const hasSynced = useRef(false);

  const queryKey = ['whatsapp', 'messages', jid ?? ''];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!jid) throw new Error('Missing chat id');
      const { data } = await api.get(`/api/whatsapp/chats/${encodeURIComponent(jid)}/messages`);
      return data;
    },
    enabled: !!jid,
    staleTime: 1000 * 60, // 1 minute
  });

  // Sync mutation to fetch historical messages from Evolution API
  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!jid) throw new Error('Missing chat id');
      const { data } = await api.post(`/api/whatsapp/chats/${encodeURIComponent(jid)}/sync`);
      return data;
    },
    onSuccess: () => {
      // Refetch messages after sync completes
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Auto-sync on mount (once per chat session)
  useEffect(() => {
    if (jid && !hasSynced.current) {
      hasSynced.current = true;
      syncMutation.mutate();
    }
  }, [jid]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutation = useMutation({
    mutationFn: async ({ text }: SendMessageInput) => {
      if (!jid) throw new Error('Missing chat id');
      const { data } = await api.post(`/api/whatsapp/chats/${encodeURIComponent(jid)}/messages`, {
        text,
        contactName,
      });
      return data;
    },
    onMutate: async ({ text }) => {
      const randomSuffix = typeof window !== 'undefined' && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2);
      const optimisticId = `temp-${Date.now()}-${randomSuffix}`;

      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (existing: any) => ({
        ...(existing ?? { messages: [] }),
        messages: [
          ...(existing?.messages ?? []),
          {
            id: optimisticId,
            jid: jid || '',
            text,
            timestamp: Date.now(),
            direction: 'outgoing',
            status: 'pending',
          },
        ],
      }));

      return { optimisticId, queryKey, previous };
    },
    onSuccess: (response, _variables, context) => {
      if (!context) return;
      const confirmed = response.message;

      queryClient.setQueryData(context.queryKey, (existing: any) => {
        const messages = existing?.messages ?? [];
        let next = messages.filter((msg: any) => (confirmed ? msg.id !== confirmed.id : true));
        next = next.map((msg: any) => {
          if (msg.id !== context.optimisticId) return msg;
          if (confirmed) {
            return { ...confirmed, status: 'sent' };
          }
          return { ...msg, status: 'sent' };
        });
        return { ...existing, messages: next };
      });

      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'chats'] });
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      queryClient.setQueryData(context.queryKey, (existing: any) => {
        const messages = existing?.messages ?? [];
        return {
          ...existing,
          messages: messages.map((msg: any) =>
            msg.id === context.optimisticId
              ? { ...msg, status: 'error' }
              : msg
          ),
        };
      });
    },
  });

  return {
    ...query,
    sendMessage: mutation,
  };
}

export function useSendMessage(jid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (text: string) => {
      const { data } = await api.post(`/api/whatsapp/chats/${encodeURIComponent(jid)}/messages`, {
        text,
      });
      return data;
    },
    onMutate: async (text) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['whatsapp', 'messages', jid] });

      const previousMessages = queryClient.getQueryData(['whatsapp', 'messages', jid]);

      queryClient.setQueryData(['whatsapp', 'messages', jid], (old: any) => ({
        ...(old ?? { messages: [] }),
        messages: [
          ...(old?.messages || []),
          {
            id: `temp-${Date.now()}`,
            direction: 'outgoing',
            text: text,
            timestamp: Date.now(),
            status: 'pending',
          },
        ],
      }));

      return { previousMessages };
    },
    onError: (err, text, context) => {
      // Rollback on error
      queryClient.setQueryData(['whatsapp', 'messages', jid], context?.previousMessages);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages', jid] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'chats'] });
    },
  });
}
